'use client'

import { useEffect, useState, type ReactNode } from 'react'
import { I18nProvider, useI18n } from '@/components/i18n-provider'
import { Toaster } from '@/components/ui/toaster'
import { ChatAssistant } from '@/components/ai/chat-assistant'
import { type Locale, defaultLocale, locales } from '@/lib/i18n'

function getCookieLocale(): Locale {
  if (typeof document === 'undefined') return defaultLocale

  const cookiePair = document.cookie
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.startsWith('locale='))

  if (!cookiePair) {
    return defaultLocale
  }

  const value = decodeURIComponent(cookiePair.substring('locale='.length))
  return (locales as readonly string[]).includes(value) ? (value as Locale) : defaultLocale
}

function LocaleSync() {
  const { locale } = useI18n()

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale
    }
  }, [locale])

  return null
}

export function AppProviders({ children }: { children: ReactNode }) {
  const [initialLocale] = useState(() => {
    const locale = getCookieLocale()
    if (typeof document !== 'undefined') {
      try {
        localStorage.setItem('locale', locale)
      } catch {
        // ignore
      }
    }
    return locale
  })

  return (
    <I18nProvider initialLocale={initialLocale}>
      {children}
      <LocaleSync />
      <Toaster />
      <ChatAssistant />
    </I18nProvider>
  )
}
